import cx from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { Typography } from '../../../design-system/components/typography/Typography';
import { Carousel, useCarousel } from '../../../domains/Layout/Carousel/Carousel';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { DestinationContents } from '../../../resources/destinationContents/domain/types/destinationContents';
import { getFacetTagCategoryTrackingProperty } from '../../../resources/facet/domain/operations/getFacetTagCategoryTrackingProperty';
import { getFacetTagNameTrackingProperty } from '../../../resources/facet/domain/operations/getFacetTagNameTrackingProperty';
import { Facet } from '../../../resources/facet/domain/types/facet';
import { FacetCard } from '../../../shared/components/FacetCard/FacetCard';
import { IntrinsicElement } from '../../../shared/utilities/types/IntrinsicElement';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './Facets.module.css';

type FacetsProps = {
    destination: PickDomain<Destination, 'name' | 'preposition'>;
    facets: Array<
        PickDomain<
            Facet,
            'id' | 'slug' | 'title' | 'coverImage' | 'squareImage' | 'filter',
            {
                destination: Pick<Destination, 'id' | 'slug' | 'name'>;
            }
        >
    >;
    facetSectionContent?: DestinationContents['facetSectionContent'] | null;
    titleElement?: IntrinsicElement;
    facetTitleElement?: IntrinsicElement;
};

export function Facets({
    destination,
    facets,
    facetSectionContent,
    titleElement,
    facetTitleElement,
}: FacetsProps) {
    const { formatMessage } = useIntl();
    const carousel = useCarousel();

    return (
        <>
            <Typography as={titleElement} scale="primary-l-extra-bold" className={style.title}>
                {formatMessage(
                    { id: 'page.destination.facets.title' },
                    {
                        destinationPreposition: destination.preposition,
                        destinationName: destination.name,
                    },
                )}
            </Typography>

            {facetSectionContent ? (
                <div className={cx('row', style.facetRow)}>
                    <div className="col-12 col-lg-6">
                        <HtmlContent htmlContent={facetSectionContent} />
                    </div>
                </div>
            ) : null}

            <Carousel
                {...carousel}
                slidesSize="large"
                slidesVisibleRules={{
                    sm: 2,
                    md: 2,
                    lg: 3,
                    xl: 4,
                    xxl: 6,
                }}
                trackingInformation={{
                    navClick: {
                        block: 'facets',
                    },
                }}
            >
                {facets.map((facet, index) => {
                    return (
                        <FacetCard
                            key={facet.id}
                            facet={facet}
                            titleElement={facetTitleElement}
                            image={{
                                sizes: '(min-width: 992px) 300px, (min-width: 768px) 200px, (min-width: 576px) 250px, 300px',
                            }}
                            trackingInformation={{
                                click: {
                                    block: 'facets',
                                    destination_id: facet.destination.id,
                                    destination_slug: facet.destination.slug,
                                    facet_id: facet.id,
                                    card_number: index + 1,
                                    row_number: 0,
                                    facet_tag_category: getFacetTagCategoryTrackingProperty(facet),
                                    facet_tag_name: getFacetTagNameTrackingProperty(facet),
                                },
                            }}
                        />
                    );
                })}
            </Carousel>
        </>
    );
}
